import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { BadgeType, DateType } from '@/types/gamification';
import { subDays, differenceInWeeks, parseISO } from 'date-fns';

interface BadgeCheckResult {
  earned: boolean;
  badgeKey?: BadgeType;
  badgeName?: string;
  points?: number;
}

export function useBadgeAwarder() {
  const { user } = useAuth();

  // Helper to check if user already has a badge
  const hasBadge = useCallback(async (badgeKey: BadgeType): Promise<boolean> => {
    if (!user) return true; // Return true to prevent awarding if no user

    const { data: badge } = await supabase
      .from('badges')
      .select('id')
      .eq('badge_key', badgeKey)
      .maybeSingle();

    if (!badge) return true;

    const { data: userBadge } = await supabase
      .from('user_badges')
      .select('id')
      .eq('user_id', user.id)
      .eq('badge_id', badge.id)
      .maybeSingle();

    return !!userBadge;
  }, [user]);

  // Award a badge to the user
  const awardBadge = useCallback(async (badgeKey: BadgeType): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    // Check if already earned
    const alreadyHas = await hasBadge(badgeKey);
    if (alreadyHas) return { earned: false };

    // Get badge details
    const { data: badge } = await supabase
      .from('badges')
      .select('*')
      .eq('badge_key', badgeKey)
      .maybeSingle();

    if (!badge) return { earned: false };

    // Award the badge
    const { error: insertError } = await supabase
      .from('user_badges')
      .insert({
        user_id: user.id,
        badge_id: badge.id,
      });

    if (insertError) {
      console.error('Failed to award badge:', insertError);
      return { earned: false };
    }

    // user_levels is now updated automatically by a database trigger on user_badges
    // insert (server-side, can't be tampered with). No client-side update needed.


    return {
      earned: true,
      badgeKey: badgeKey,
      badgeName: badge.name,
      points: badge.points,
    };
  }, [user, hasBadge]);

  // Check for First Expression badge (first announcement)
  const checkFirstExpression = useCallback(async (): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('first_expression');
    if (alreadyHas) return { earned: false };

    // If we're here, they just created their first announcement
    return awardBadge('first_expression');
  }, [user, hasBadge, awardBadge]);

  // Check for Love Ambassador badge (8+ announcements in 14 days)
  const checkLoveAmbassador = useCallback(async (): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('love_ambassador');
    if (alreadyHas) return { earned: false };

    const fourteenDaysAgo = subDays(new Date(), 14).toISOString();

    const { data: recentAnnouncements, error } = await supabase
      .from('announcements')
      .select('id')
      .eq('user_id', user.id)
      .gte('created_at', fourteenDaysAgo);

    if (error || !recentAnnouncements) return { earned: false };

    if (recentAnnouncements.length >= 8) {
      return awardBadge('love_ambassador');
    }

    return { earned: false };
  }, [user, hasBadge, awardBadge]);

  // Check for Relationship Celebrant badge (announcement for spouse)
  const checkRelationshipCelebrant = useCallback(async (relationship: string): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('relationship_celebrant');
    if (alreadyHas) return { earned: false };

    if (relationship === 'spouse') {
      return awardBadge('relationship_celebrant');
    }

    return { earned: false };
  }, [user, hasBadge, awardBadge]);

  // Check for Birthday Champion badge (added a birthday date)
  const checkBirthdayChampion = useCallback(async (dateType: DateType): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('birthday_champion');
    if (alreadyHas) return { earned: false };

    if (dateType === 'birthday') {
      return awardBadge('birthday_champion');
    }

    return { earned: false };
  }, [user, hasBadge, awardBadge]);

  // Check for Friendship Keeper badge (added a friendship anniversary)
  const checkFriendshipKeeper = useCallback(async (dateType: DateType): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('friendship_keeper');
    if (alreadyHas) return { earned: false };

    if (dateType === 'friendship_anniversary') {
      return awardBadge('friendship_keeper');
    }

    return { earned: false };
  }, [user, hasBadge, awardBadge]);

  // Check for Consistency Star badge (announcements in 3+ different weeks)
  const checkConsistencyStar = useCallback(async (): Promise<BadgeCheckResult> => {
    if (!user) return { earned: false };

    const alreadyHas = await hasBadge('consistency_star');
    if (alreadyHas) return { earned: false };

    const { data: announcements, error } = await supabase
      .from('announcements')
      .select('created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (error || !announcements || announcements.length < 3) return { earned: false };

    // Count unique weeks
    const uniqueWeeks = new Set<number>();
    const referenceDate = new Date(2020, 0, 1); // Use a fixed reference date

    announcements.forEach(a => {
      const announcementDate = parseISO(a.created_at);
      const weekNumber = differenceInWeeks(announcementDate, referenceDate);
      uniqueWeeks.add(weekNumber);
    });

    if (uniqueWeeks.size >= 3) {
      return awardBadge('consistency_star');
    }

    return { earned: false };
  }, [user, hasBadge, awardBadge]);

  // Check all announcement-related badges after creating/publishing
  // Returns earned badges so UI can show JacKey nudges
  const checkAnnouncementBadges = useCallback(async (relationship: string, isFirstAnnouncement: boolean) => {
    const results: BadgeCheckResult[] = [];

    // Check first expression (only on first announcement)
    if (isFirstAnnouncement) {
      const firstResult = await checkFirstExpression();
      if (firstResult.earned) {
        results.push(firstResult);
      }
    }

    // Check relationship celebrant
    const relationshipResult = await checkRelationshipCelebrant(relationship);
    if (relationshipResult.earned) {
      results.push(relationshipResult);
    }

    // Check love ambassador
    const loveResult = await checkLoveAmbassador();
    if (loveResult.earned) {
      results.push(loveResult);
    }

    // Check consistency star
    const consistencyResult = await checkConsistencyStar();
    if (consistencyResult.earned) {
      results.push(consistencyResult);
    }

    return results;
  }, [checkFirstExpression, checkRelationshipCelebrant, checkLoveAmbassador, checkConsistencyStar]);

  // Check date-related badges after adding a date
  // Returns earned badges so UI can show JacKey nudges
  const checkDateBadges = useCallback(async (dateType: DateType) => {
    const results: BadgeCheckResult[] = [];

    // Check birthday champion
    const birthdayResult = await checkBirthdayChampion(dateType);
    if (birthdayResult.earned) {
      results.push(birthdayResult);
    }

    // Check friendship keeper
    const friendshipResult = await checkFriendshipKeeper(dateType);
    if (friendshipResult.earned) {
      results.push(friendshipResult);
    }

    return results;
  }, [checkBirthdayChampion, checkFriendshipKeeper]);

  return {
    checkAnnouncementBadges,
    checkDateBadges,
    awardBadge,
  };
}
